import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { 
  Building, 
  Users, 
  Award, 
  Clock, 
  Phone,
  Mail,
  MapPin,
  Home,
  Wrench,
  HardHat,
  CheckCircle,
  Star,
  Shield
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";

// Import images
import hamiltonConstruction from "@/assets/hamilton-construction.jpg";
import residentialConstruction from "@/assets/residential-construction.jpg";
import generalContractingProjects from "@/assets/general-contracting-projects.jpg";

const Hamilton = () => {
  const cityData = {
    name: "Hamilton",
    region: "Golden Horseshoe",
    population: "540K",
    slug: "hamilton-general-contracting",
    intro: "Hamilton, Ontario's historic steel city, is undergoing rapid urban renewal. Blue Knight Contracting provides industrial renovations, commercial projects, and residential construction across Hamilton.",
    featured_services: [
      "Urban Revitalization",
      "Industrial Renovation",
      "Residential Construction",
      "Commercial Development"
    ],
    nearby_areas: ["Stoney Creek", "Burlington", "Grimsby", "Brantford"],
    cta: "Start your Hamilton construction project with us today."
  };

  const faqData = [
    {
      question: "What types of urban revitalization projects do you handle in Hamilton?",
      answer: "We handle comprehensive urban revitalization projects in Hamilton including downtown redevelopment, heritage building restoration, brownfield remediation, and mixed-use developments in the city core."
    },
    {
      question: "Can you renovate industrial facilities in Hamilton?",
      answer: "Yes, we specialize in industrial renovation and adaptive reuse projects, transforming old manufacturing facilities, steel industry buildings, and warehouses into modern commercial and residential spaces."
    },
    {
      question: "Do you build new residential construction in Hamilton?",
      answer: "Absolutely. We provide comprehensive residential construction services including custom homes, infill development, multi-unit housing, and residential renovations throughout Hamilton and surrounding areas."
    },
    {
      question: "What areas around Hamilton do you serve?",
      answer: "We serve Hamilton and the surrounding Golden Horseshoe region including Stoney Creek, Burlington, Grimsby, Brantford, and other communities in the Niagara and western Ontario areas."
    },
    {
      question: "Can you handle commercial development in Hamilton's downtown?",
      answer: "Yes, we have extensive experience with commercial development in Hamilton's downtown core, including office buildings, retail spaces, restaurants, and mixed-use developments."
    },
    {
      question: "Do you work with Hamilton's heritage preservation requirements?",
      answer: "Yes, our team has experience working with Hamilton's heritage preservation guidelines and requirements for projects involving heritage buildings and designated areas."
    }
  ];

  const serviceData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Blue Knight Contracting Hamilton",
    "description": "Professional general contracting and construction services in Hamilton and the Golden Horseshoe. Specializing in urban revitalization, industrial renovation, and residential construction.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Hamilton",
      "addressRegion": "Ontario",
      "addressCountry": "Canada"
    },
    "telephone": "+1-647-760-8854",
    "url": "https://blueknightcontracting.ca/hamilton",
    "areaServed": {
      "@type": "City",
      "name": "Hamilton"
    },
    "serviceType": [
      "General Contracting Hamilton",
      "Urban Revitalization",
      "Industrial Renovation Hamilton",
      "Residential Construction Hamilton",
      "Commercial Development"
    ]
  };

  const constructionServices = [
    {
      title: "Urban Revitalization",
      description: "Downtown redevelopment and urban renewal projects transforming Hamilton's city core.",
      icon: Building,
      features: [
        "Downtown redevelopment",
        "Heritage building restoration",
        "Brownfield remediation",
        "Mixed-use developments",
        "Transit-oriented development",
        "Community infrastructure"
      ]
    },
    {
      title: "Industrial Renovation",
      description: "Adaptive reuse and renovation of Hamilton's industrial heritage buildings.",
      icon: Wrench,
      features: [
        "Manufacturing facility conversion",
        "Warehouse renovations",
        "Steel industry adaptations",
        "Industrial heritage preservation",
        "Modern facility upgrades",
        "Environmental remediation"
      ]
    },
    {
      title: "Residential Construction",
      description: "Custom homes and residential developments throughout Hamilton and area.",
      icon: Home,
      features: [
        "Custom home construction",
        "Infill development",
        "Multi-unit housing",
        "Heritage home renovation",
        "Affordable housing",
        "Energy-efficient builds"
      ]
    },
    {
      title: "Commercial Development",
      description: "Modern commercial spaces and business developments in Hamilton's growing economy.",
      icon: HardHat,
      features: [
        "Office buildings",
        "Retail developments",
        "Restaurant construction",
        "Healthcare facilities",
        "Educational buildings",
        "Technology centers"
      ]
    }
  ];

  return (
    <>
      <SchemaMarkup type="LocalBusiness" data={serviceData} />
      <SchemaMarkup type="FAQ" data={faqData} />
      
      <Header />
      
      <main className="min-h-screen">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary via-primary-dark to-secondary py-24 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <Badge variant="secondary" className="mb-4 bg-white/10 text-white border-white/20">
                  Hamilton General Contracting
                </Badge>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                  General Contracting & Construction in Hamilton
                </h1>
                <p className="text-xl lg:text-2xl mb-8 text-white/90 leading-relaxed">
                  {cityData.intro}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                    <Link to="/contact">Get Hamilton Quote</Link>
                  </Button>
                  <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                    <a href="tel:+16477608854">Call: (647) 760-8854</a>
                  </Button>
                </div>
                <div className="flex flex-wrap gap-6 text-white/80">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    <span>Population: {cityData.population}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    <span>{cityData.region}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    <span>Licensed & Insured</span>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={hamiltonConstruction} 
                  alt="Hamilton waterfront with steel industry redevelopment" 
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Featured Services */}
        <section className="py-20 bg-gradient-to-b from-background to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Hamilton Construction Services</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Professional Construction Services in Hamilton
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                From urban revitalization to industrial renovation, we're helping transform Hamilton's landscape with quality construction services.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {constructionServices.map((service, index) => (
                <Card key={index} className="h-full hover:shadow-lg transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                  <CardHeader>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 rounded-full bg-primary/10">
                        <service.icon className="h-8 w-8 text-primary" />
                      </div>
                    </div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription className="text-base">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Services List */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Our Specialties</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Featured Hamilton Construction Services
              </h2>
              <p className="text-xl text-muted-foreground">
                Specialized construction services for Hamilton's urban renewal and industrial transformation.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {cityData.featured_services.map((service, index) => (
                <Card key={index} className="group hover:shadow-lg transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                  <CardContent className="p-6 text-center">
                    <div className="p-3 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                      <CheckCircle className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{service}</h3>
                    <p className="text-muted-foreground text-sm">
                      Professional {service.toLowerCase()} services in Hamilton and the Golden Horseshoe.
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Nearby Service Areas */}
        <section className="py-20 bg-gradient-to-b from-secondary/5 to-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Golden Horseshoe Service Areas</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Serving Hamilton and Surrounding Areas
              </h2>
              <p className="text-xl text-muted-foreground">
                Professional construction services throughout Hamilton and the Golden Horseshoe region.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                <Card className="text-center p-4 hover:shadow-md transition-shadow border-0 bg-primary/5">
                  <div className="flex items-center justify-center gap-2">
                    <Star className="h-4 w-4 text-primary" />
                    <span className="font-semibold">Hamilton</span>
                  </div>
                </Card>
                {cityData.nearby_areas.map((area, index) => (
                  <Card key={index} className="text-center p-4 hover:shadow-md transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                    <div className="flex items-center justify-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      <span className="font-medium">{area}</span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Frequently Asked Questions</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Hamilton Construction FAQ
              </h2>
              <p className="text-xl text-muted-foreground">
                Common questions about construction services in Hamilton and the Golden Horseshoe.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto grid gap-6">
              {faqData.map((faq, index) => (
                <Card key={index} className="border-0 bg-white/60 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="text-left text-lg">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-primary via-primary-dark to-secondary text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Ready to Start Your Hamilton Construction Project?
              </h2>
              <p className="text-xl mb-8 text-white/90">
                {cityData.cta} Get professional construction services from Hamilton's trusted general contractors.
              </p>
              
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <Phone className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Call Us</h3>
                  <p className="text-white/80">
                    <a href="tel:+16477608854" className="hover:text-white transition-colors">
                      (647) 760-8854
                    </a>
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <Mail className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Email Us</h3>
                  <p className="text-white/80">
                    <a href="mailto:info@blueknightcontracting.ca" className="hover:text-white transition-colors">
                      info@blueknightcontracting.ca
                    </a>
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <MapPin className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Service Area</h3>
                  <p className="text-white/80">Hamilton & Golden Horseshoe</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                  <Link to="/contact">Get Hamilton Construction Quote</Link>
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                  <Link to="/gallery">View Our Hamilton Projects</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default Hamilton;